/**
 * Sequential Iris-enabled agent link for bulk-add.
 *
 * Per Multi-Add Phase 1 manual QA — bulk add for Iris-enabled cameras:
 * Iris-enabled cameras advertise their own embedded agent over mDNS;
 * "adding" one to a studio is really an agent-link operation (the
 * camera joins the studio's agent fleet, then state-sync delivers the
 * resulting device records). Unlike ONVIF, there is NO discovery-time
 * advertisement that we can probe anonymously — every link request
 * requires the device's password upfront. Single-add at
 * `add/source/dialogs/credentials-dialog.tsx` already encodes this
 * contract; this helper mirrors the same primitives
 * (`directLinkStatusAndRequestAgent` + `linkAgent`) but as a non-React
 * sequential operation the bulk pipeline can await.
 *
 * Per `think-in-systems` (ai.knowledge/global): the helper is the
 * only place that touches the two-step mDNS-then-link dance for
 * bulk-add. `wiring.ts` consumes the discriminated result and routes
 * (success → count as added, failed → skip with reason).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import { toHexFromId } from '#~/api/utils.js';
import {
  directLinkStatusAndRequestAgent,
  linkAgent,
  type LinkRequest
} from '#~/api/agent/index.js';
import { directLinkErrorCode } from '#~/agent/components/bridge/direct-link-log.js';
import { classifyDirectLinkRequestFailure } from '#~/agent/components/bridge/direct-link-transport-error.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { logger } from '#~/logger/index.js';
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';

/** Which leg of an Iris link `error` failed — the part the user can act on. */
export type LinkIrisFailureCause =
  /** The camera never answered: off, on another subnet, or an unresolvable host. */
  | 'unreachable'
  /** The camera answered with an error or an incomplete reply. */
  | 'camera'
  /** The camera accepted the credentials but Iris could not register the link. */
  | 'cloud'
  /** No more specific cause could be established. */
  | 'unknown';

/**
 * Discriminated result. Callers route success → count as added,
 * failed → increment skippedCount and (optionally) surface reason in
 * the final toast or analytics event.
 *
 * Reasons:
 *   - `locked`         — the camera's device-login is in lockout (too
 *                        many wrong attempts). `lockoutSeconds` carries
 *                        the remaining cooldown.
 *   - `auth`           — credentials rejected by the camera: an in-band
 *                        `error` payload (`message` is the camera's own
 *                        wording) or its link API answering 401/403.
 *   - `already-linked` — `agentDirectStatus.linked === true` means the
 *                        camera is currently registered to a different
 *                        studio. Bulk-add deliberately does NOT prompt
 *                        for steal-from-other-studio here (single-add
 *                        does, but it owns the modal and the user is
 *                        already focused on that single device). The
 *                        bulk pipeline skips with this reason; the
 *                        user can resolve manually via single-add.
 *   - `error`          — anything else. `cause` names the failed leg;
 *                        `message` is the underlying text (IPC, transport,
 *                        API) for logs only and is never shown to users.
 */
export type LinkIrisResult =
  | {
      kind: 'success';
      agentId: Agent['id'];
    }
  | {
      kind: 'failed';
      reason: 'locked' | 'auth' | 'already-linked' | 'error';
      message?: string;
      lockoutSeconds?: number;
      cause?: LinkIrisFailureCause;
      /** HTTP status the camera or the Iris API answered with, when known. */
      status?: number;
    };

/** Helper-injected dependencies. Greppable single object per call site. */
export interface LinkDeps {
  /** Room id the linked agent is bound to. */
  roomId: Room['id'];
  /** Auth0 access token for the `linkAgent` mutation (NOT the device token). */
  token: string;
  /**
   * Bug 2 (spec §2.3): confirm an unlink-then-relink when the camera is already
   * bound to another studio. When supplied AND it resolves `true`, the
   * already-linked bail FALLS THROUGH to the same extraction + `linkAgent` the
   * unlinked path uses — relink is implicit cloud-side over the SAME
   * `AgentRegistryLink` call (no unlink, no new API). Absent or `false` ⇒ the
   * unchanged already-linked bail, so callers that omit it (the bulk pipeline)
   * stay no-prompt. The Authentication tab supplies the single-add
   * `prompt.confirm` here (`authentication-content.tsx`).
   */
  confirmRelink?: () => Promise<boolean>;
}

/**
 * Predicate: is this discovery the kind the bulk pipeline must
 * agent-link (Iris-enabled) vs. ONVIF-probe vs. straight-commit?
 *
 * Iris-enabled rows carry `irisEnabled === true` regardless of whether
 * they came from the raw discovery slice (selector.tsx ~line 287) or
 * the iris-service-derived branch — both shapes expose the flag at the
 * same top-level path. Exported so `wiring.ts` can route without
 * re-deriving the predicate.
 */
export const isIrisDiscovery = (discovery: Discovery): boolean =>
  !!(discovery as { irisEnabled?: boolean }).irisEnabled;

/**
 * The camera's own link endpoint. `agentId` is not a host: once the Studio's
 * bridge has also discovered the camera, `buildIrisDiscoveries` stores that
 * bridge's agent id there, and a link sent to it never reaches the camera. A
 * legacy `host:port` address keeps only the host and supplies the port.
 */
const irisLinkEndpoint = (discovery: Discovery): { address: string; port?: number } => {
  const video =
    'video' in discovery && discovery.video
      ? (discovery.video as { host?: string; port?: number })
      : undefined;
  const raw = discovery.address ?? video?.host ?? '';
  const packed = /^([^:]+):(\d+)$/.exec(raw);
  const port =
    (discovery as { port?: number }).port ??
    video?.port ??
    (packed ? Number(packed[2]) : undefined);

  return { address: packed?.[1] ?? raw, ...(port === undefined ? {} : { port }) };
};

/** Map a rejected camera request (the IPC proxy to `/api/v1/link`) onto a result. */
const cameraRequestFailure = (message: string): LinkIrisResult => {
  const failure = classifyDirectLinkRequestFailure(message);
  switch (failure.kind) {
    case 'credentials':
      return { kind: 'failed', reason: 'auth', status: failure.status };
    case 'unreachable':
      return { kind: 'failed', reason: 'error', cause: 'unreachable', message };
    case 'camera':
      return { kind: 'failed', reason: 'error', cause: 'camera', status: failure.status, message };
    default:
      return { kind: 'failed', reason: 'error', cause: 'unknown', message };
  }
};

/**
 * Link an Iris-enabled camera's embedded agent to the current studio.
 * The discovery's host:port is what the studio's Electron mDNS proxy
 * speaks to; on success the device's bridge agent becomes part of the
 * studio's agent fleet and state-sync starts delivering the camera's
 * own device records to the room — no `addDevice` call is needed
 * downstream.
 *
 * Resolves with `success` when both the device-login probe and the
 * cloud `linkAgent` mutation complete cleanly; otherwise resolves with
 * `failed` carrying a discriminated reason. Never throws — the caller
 * decides whether to skip the device or surface an error toast.
 *
 * @param discovery   — Iris-enabled discovery row.
 * @param credentials — credentials the user typed into the prompt.
 *   Required (Iris always needs auth — caller should NOT call this
 *   without first collecting creds, unlike `probeOnvifDevice` which
 *   has a credentials-less first attempt).
 * @param deps        — `roomId` and OAuth `token`.
 */
export const linkIrisDevice = async (
  discovery: Discovery,
  credentials: { username: string; password: string },
  deps: LinkDeps
): Promise<LinkIrisResult> => {
  // The Electron mDNS proxy builds `http://${service.address}:${service.port}`,
  // so hand it the camera's bare host and its link port (see irisLinkEndpoint).
  const { address, port } = irisLinkEndpoint(discovery);
  // Which leg a thrown failure came from: the camera request or the cloud link.
  let leg: 'camera' | 'cloud' = 'camera';

  try {
    // ---- Step 1: device-login probe + link-token request ----
    //
    // `directLinkStatusAndRequestAgent` goes through Electron's mDNS
    // proxy to talk to the camera's embedded link API. The camera
    // validates the credentials and (on success) issues a one-shot
    // link token the cloud `linkAgent` mutation will consume.
    const { agentDirectStatus, agentDirectLinkDetails } = await directLinkStatusAndRequestAgent({
      service: { ...discovery, address, port } as never,
      username: credentials.username,
      password: credentials.password
    });

    // Lockout takes precedence over an auth error because the camera
    // intentionally suppresses the auth-error payload when locked
    // (returns the lockout window so the UI can show "try again in
    // Xs"). Surfacing lockout as a distinct reason lets the toast
    // wording differ from generic-auth-failed.
    const lockoutStatus = agentDirectLinkDetails?.deviceLoginLockoutStatus;
    if (lockoutStatus?.locked) {
      return {
        kind: 'failed',
        reason: 'locked',
        lockoutSeconds: lockoutStatus.unlockTimeSeconds
      };
    }

    const lkError = agentDirectLinkDetails?.error;
    if (lkError) {
      // The camera's own wording is shown as-is; without one the caller uses
      // its credentials copy rather than an internal token.
      return {
        kind: 'failed',
        reason: 'auth',
        ...(lkError.message ? { message: lkError.message } : {})
      };
    }

    // `agentDirectStatus.linked === true` means the device is already
    // bound to a different studio. Bug 2 (spec §2.3): the Authentication tab
    // supplies `confirmRelink` — the SAME single-add unlink-then-relink
    // confirmation (credentials-dialog.tsx ~line 145-169) — so an already-linked
    // camera can be relinked instead of dead-ending. When confirmed, fall
    // through to the extraction + `linkAgent` below: relink is implicit
    // cloud-side over the same `AgentRegistryLink` call (no unlink, no new API;
    // everything a relink needs is already in hand from the probe). Absent or
    // declined ⇒ the original bail, so the bulk pipeline (which supplies no
    // seam) stays no-prompt and its tests are untouched.
    if (agentDirectStatus?.linked) {
      const relinkConfirmed = deps.confirmRelink ? await deps.confirmRelink() : false;
      if (!relinkConfirmed) {
        return { kind: 'failed', reason: 'already-linked' };
      }
    }

    // Extract bridge agent id and link request id. Both are protobuf
    // ByteIds in the response — `toHexFromId` converts to the studio's
    // canonical hex string form (mirrors `useConnectToMDNSService` ~
    // line 561-565 in `agent/components/bridge/hooks.ts`).
    const agentId = toHexFromId(agentDirectLinkDetails?.bridgeId) as T_Maybe<Agent['id']>;
    const requestId = toHexFromId(agentDirectLinkDetails?.requestId) as T_Maybe<LinkRequest['id']>;

    if (!agentId || !requestId) {
      return {
        kind: 'failed',
        reason: 'error',
        cause: 'camera',
        message: 'link-response-missing-ids'
      };
    }

    // ---- Step 2: commit the link via the cloud registry ----
    //
    // `linkAgent` writes the bridge↔room binding into the studio's
    // agent registry. After this resolves, the studio's state-sync
    // subscription picks up the new agent on the next tick and its
    // advertised devices start surfacing in the room view on their
    // own. We don't need a `Commands.add` here — that's the contract
    // difference vs. the ONVIF / RTSP / NDI path where the studio is
    // the one creating the device record.
    leg = 'cloud';
    await linkAgent(agentId, requestId, deps.roomId, deps.token);

    return { kind: 'success', agentId };
  } catch (error) {
    // wrapApiCall rejects with the API's `{ code, message }` error; the IPC
    // proxy rejects with an Error whose message carries the transport/HTTP cause.
    const message = (error as Error)?.message ?? 'link-rejected';
    // The raw text names the real cause and the dialled endpoint; it goes to the
    // app log only, never to the user.
    logger.warning(
      `Iris link failed for ${discovery.name} (${leg} leg, camera ${address}:${port ?? 'default port'}): ${message}`
    );
    if (leg === 'camera') {
      return cameraRequestFailure(message);
    }

    const status = directLinkErrorCode(error);
    return {
      kind: 'failed',
      reason: 'error',
      cause: 'cloud',
      message,
      ...(status === undefined ? {} : { status })
    };
  }
};
